import { useEffect } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { IconProps } from "@project/ui";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@project/ui";
import { Button } from "@project/ui";
import { Icon, cn, formatShort } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { fetchDashboardSummary, homeExpenseQueryKeys } from "@apps/dashboard/lib/home-expense-api";
import { currentYearMonth } from "@apps/dashboard/lib/year-month";
import { expenseEffectiveMinor } from "@apps/dashboard/lib/expense-effective-amount";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import {
  clearProductTourPending,
  peekProductTourPending,
} from "@apps/dashboard/features/onboarding/product-tour-storage";
import { startDashboardProductTour } from "@apps/dashboard/features/onboarding/start-dashboard-product-tour";

type InsightKind = "neutral" | "within" | "tight" | "over";

function resolveInsight(
  budgetProgressPercent: number | null | undefined,
  hasBudgetCap: boolean,
): { kind: InsightKind; percent: number } {
  if (!hasBudgetCap || budgetProgressPercent == null) {
    return { kind: "neutral", percent: 0 };
  }
  const p = Math.round(budgetProgressPercent);
  if (p > 100) return { kind: "over", percent: p };
  if (p >= 85) return { kind: "tight", percent: p };
  return { kind: "within", percent: p };
}

export default function HomePage(): JSX.Element {
  const { t, locale } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const { activeHouseId, houses, housesLoading } = useActiveHouse();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Tour: (1) `?tour=1` tras onboarding, (2) `sessionStorage` al pulsar «Ver tour» en Ajustes.
   * Sin cleanup del timeout: en React StrictMode el cleanup cancelaba el tour y no se veía nada.
   */
  useEffect(() => {
    const fromQuery = searchParams.get("tour") === "1";
    const fromSettings = peekProductTourPending();
    if (!fromQuery && !fromSettings) return;
    if (housesLoading || houses === undefined) return;

    if (fromQuery) {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete("tour");
          return n;
        },
        { replace: true },
      );
    }

    const delayMs = fromSettings ? 350 : 200;
    window.setTimeout(() => {
      clearProductTourPending();
      startDashboardProductTour((key: string) => t(key), navigate, {
        tourHouseId: activeHouseId ?? houses[0]?.id ?? null,
        hasAnyHouse: houses.length > 0,
      });
    }, delayMs);
  }, [searchParams, setSearchParams, t, navigate, houses, housesLoading, activeHouseId]);

  const dashboardYm = currentYearMonth();

  const summaryQuery = useQuery({
    queryKey:
      activeHouseId != null
        ? homeExpenseQueryKeys.dashboardSummary(locale, activeHouseId, dashboardYm)
        : ["dashboard", "summary", "disabled"],
    queryFn: () => {
      if (!activeHouseId) throw new Error("no house");
      return fetchDashboardSummary(locale, activeHouseId, dashboardYm);
    },
    enabled: activeHouseId != null,
  });

  if (!activeHouseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <h1 className="font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.pages.home")}</h1>
        <div className="mt-8 flex flex-col items-center gap-4 rounded-2xl bg-surface-container-low p-10 text-center outline outline-1 outline-outline-variant/15">
          <Icon name="Home" className="size-14 text-on-surface-variant/40" strokeWidth={1.5} />
          <p className="font-body text-body-md text-on-surface-variant">{t("dashboardUi.houses.emptyHint")}</p>
          <Button asChild className="font-headline font-semibold shadow-md">
            <Link to={dashboardPaths.houses}>{t("dashboardUi.houses.pageTitle")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (summaryQuery.isLoading) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <LoadingState />
      </div>
    );
  }

  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState onRetry={() => void summaryQuery.refetch()} />
      </div>
    );
  }

  const s = summaryQuery.data;
  const currency = s.currency;
  const fmt = (minor: number) => formatMinorAsCurrency(minor, currency, locale);
  const hasBudgetDenominator =
    s.plannedBudgetMinor > 0 ||
    (s.monthlyBudgetCapMinor != null && s.monthlyBudgetCapMinor > 0);
  const insight = resolveInsight(s.budgetProgressPercent, hasBudgetDenominator);
  const capMinor = s.monthlyBudgetCapMinor ?? 0;
  const showBudgetSetupUrgent = s.plannedBudgetMinor <= 0 && capMinor <= 0;
  const showBudgetSetupCapOnly = s.plannedBudgetMinor <= 0 && capMinor > 0;
  const openingBalanceMinor = s.openingBankBalanceMinor;
  const monthNeedMinor =
    s.plannedBudgetMinor > 0
      ? s.plannedBudgetMinor
      : s.monthlyBudgetCapMinor != null && s.monthlyBudgetCapMinor > 0
        ? s.monthlyBudgetCapMinor
        : 0;
  const retainedMinor = s.proratedReserveMinor ?? 0;
  const projectedBankBalanceMinor =
    openingBalanceMinor + monthNeedMinor - s.spentMinor - retainedMinor;
  const barPct = Math.min(
    100,
    Math.max(0, Math.round(s.budgetProgressPercent ?? 0)),
  );

  const insightTitle =
    insight.kind === "within"
      ? t("dashboardUi.home.insightTitleWithin")
      : insight.kind === "tight"
        ? t("dashboardUi.home.insightTitleTight")
        : insight.kind === "over"
          ? t("dashboardUi.home.insightTitleOver")
          : t("dashboardUi.home.insightTitleNeutral");

  const insightBody =
    insight.kind === "within"
      ? t("dashboardUi.home.insightBodyWithin", { percent: insight.percent })
      : insight.kind === "tight"
        ? t("dashboardUi.home.insightBodyTight")
        : insight.kind === "over"
          ? t("dashboardUi.home.insightBodyOver", { percent: insight.percent })
          : t("dashboardUi.home.insightBodyNeutral");

  const journalIcons: IconProps["name"][] = ["Wrench", "Landmark", "Zap"];
  const journalIconWrap = [
    "bg-secondary/10 text-secondary",
    "bg-primary/10 text-primary",
    "bg-tertiary-container/10 text-tertiary",
  ] as const;

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <h1 className="sr-only">{t("dashboardUi.pages.home")}</h1>

      <p className="text-xs font-label uppercase tracking-[0.2em] text-slate-400">
        {t("dashboardUi.home.periodLabel", { yearMonth: s.yearMonth })}
      </p>

      {showBudgetSetupUrgent ? (
        <div className="mb-6 mt-4 rounded-2xl border border-warning-500/30 bg-warning-50 p-5 dark:bg-warning-950/25">
          <p className="font-headline text-base font-bold text-warning-800 dark:text-warning-200">
            {t("dashboardUi.home.budgetSetupUrgentTitle")}
          </p>
          <p className="mt-2 text-body-sm text-warning-900/90 dark:text-warning-100/85">
            {t("dashboardUi.home.budgetSetupUrgentBody")}
          </p>
          <Button asChild className="mt-4 font-semibold">
            <Link to={`${dashboardPaths.houseBudget(activeHouseId)}?ym=${encodeURIComponent(s.yearMonth)}`}>
              {t("dashboardUi.home.budgetSetupUrgentCta")}
            </Link>
          </Button>
        </div>
      ) : showBudgetSetupCapOnly ? (
        <div className="mb-6 mt-4 rounded-2xl border border-secondary/20 bg-secondary-container/15 p-5">
          <p className="font-headline text-base font-bold text-on-secondary-container">
            {t("dashboardUi.home.budgetSetupCapOnlyTitle")}
          </p>
          <p className="mt-2 text-body-sm text-on-secondary-container/85">
            {t("dashboardUi.home.budgetSetupCapOnlyBody")}
          </p>
          <Button variant="outline" asChild className="mt-4 border-secondary/30 font-semibold">
            <Link to={`${dashboardPaths.houseBudget(activeHouseId)}?ym=${encodeURIComponent(s.yearMonth)}`}>
              {t("dashboardUi.home.budgetSetupCapOnlyCta")}
            </Link>
          </Button>
        </div>
      ) : null}

      {/* Hero editorial (mockup: asimetría + insight) */}
      <motion.div
        className="mb-10 mt-4 flex flex-col gap-10 lg:mb-12 lg:flex-row"
        initial={prefersReducedMotion ? undefined : { opacity: 0, y: 12 }}
        animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
        transition={prefersReducedMotion ? undefined : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          className="group relative isolate flex-[1.6] overflow-hidden rounded-[2rem] bg-surface-container-lowest p-8 shadow-sm outline outline-1 outline-outline-variant/10 lg:p-10"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 10 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? undefined : { duration: 0.28, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative z-[1]">
            <span className="mb-2 block text-xs font-label uppercase tracking-[0.2em] text-slate-400">
              {t("dashboardUi.home.heroProjectedBalanceLabel")}
            </span>
            <p className="text-display-lg font-headline font-extrabold text-primary lg:text-5xl">
              {fmt(projectedBankBalanceMinor)}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <div className="flex flex-col">
                <span className="text-[10px] font-label uppercase tracking-widest text-slate-400">
                  {t("dashboardUi.home.heroStatSpentLabel")}
                </span>
                <span className="font-headline font-bold text-secondary">{fmt(s.spentSharedMinor)}</span>
              </div>
              <div className="mx-1 hidden h-8 w-px bg-outline-variant/20 sm:block" aria-hidden />
              <div className="flex flex-col">
                <span className="text-[10px] font-label uppercase tracking-widest text-slate-400">
                  {t("dashboardUi.home.heroStatForecastExpensesLabel")}
                </span>
                <span className="font-headline font-bold text-primary">{fmt(s.forecastExpensesMinor)}</span>
              </div>
              <div className="mx-1 hidden h-8 w-px bg-outline-variant/20 sm:block" aria-hidden />
              <div className="flex flex-col">
                <span className="text-[10px] font-label uppercase tracking-widest text-slate-400">
                  {t("dashboardUi.home.heroStatPlannedLabel")}
                </span>
                <span className="font-headline font-bold text-primary">{fmt(s.plannedBudgetMinor)}</span>
              </div>
            </div>
          </div>
          <div
            className="absolute -bottom-16 -right-16 z-0 size-64 rounded-full bg-primary-fixed/20 blur-3xl transition-transform duration-700 group-hover:scale-110"
            aria-hidden
          />
        </motion.div>

        <motion.div
          className="flex flex-1 flex-col justify-between rounded-[2rem] border border-secondary/10 bg-secondary-container/20 p-8 shadow-sm lg:p-10"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 10 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? undefined : { duration: 0.28, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div>
            <div className="mb-6 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                <Icon name="Sparkles" className="size-5" strokeWidth={1.5} aria-hidden />
              </div>
              <span className="text-xs font-label font-bold uppercase tracking-widest text-secondary">
                {t("dashboardUi.home.insightBadge")}
              </span>
            </div>
            <h2 className="font-headline text-2xl font-bold leading-tight text-on-secondary-container">
              {insightTitle}
            </h2>
            <p className="mt-4 font-body text-body-md leading-relaxed text-on-secondary-container/80">
              {insightBody}
            </p>
          </div>
          <Link
            to={dashboardPaths.houseExpenses(activeHouseId)}
            className="group mt-8 flex items-center gap-2 text-sm font-bold text-secondary"
          >
            {t("dashboardUi.home.insightCta")}
            <Icon
              name="ArrowRight"
              className="size-4 transition-transform group-hover:translate-x-1"
              strokeWidth={2}
              aria-hidden
            />
          </Link>

          <div
            className="mt-6 flex flex-col gap-2 border-t border-secondary/10 pt-6"
            data-tour="home-quick-actions"
          >
            <Button className="w-full justify-center font-label text-xs uppercase tracking-widest" asChild>
              <Link to={dashboardPaths.houseExpenseNew(activeHouseId)}>{t("dashboardUi.home.actionExpense")}</Link>
            </Button>
            <Button variant="outline" className="w-full justify-center font-label text-xs uppercase tracking-widest" asChild>
              <Link to={dashboardPaths.houseScan(activeHouseId)}>{t("dashboardUi.home.actionScan")}</Link>
            </Button>
            <Button variant="outline" className="w-full justify-center font-label text-xs uppercase tracking-widest" asChild>
              <Link to={dashboardPaths.houseMembers(activeHouseId)} data-tour="home-tour-members">
                {t("dashboardUi.home.actionMembers")}
              </Link>
            </Button>
          </div>
        </motion.div>
      </motion.div>

      <section className="mb-8 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5">
        <h2 className="font-headline text-base font-bold text-primary">
          {t("dashboardUi.home.moneySnapshotTitle")}
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <article className="rounded-xl bg-surface-container-low p-4">
            <p className="text-label-sm uppercase tracking-wide text-on-surface-variant">
              {t("dashboardUi.home.moneySnapshotOpening")}
            </p>
            <p className="mt-1 font-headline text-xl font-bold text-primary tabular-nums">
              {fmt(s.openingBankBalanceMinor)}
            </p>
          </article>
          <article className="rounded-xl bg-surface-container-low p-4">
            <p className="text-label-sm uppercase tracking-wide text-on-surface-variant">
              {t("dashboardUi.home.moneySnapshotNeedMonth")}
            </p>
            <p className="mt-1 font-headline text-xl font-bold text-primary tabular-nums">
              {fmt(monthNeedMinor)}
            </p>
          </article>
          <article className="rounded-xl bg-surface-container-low p-4">
            <p className="text-label-sm uppercase tracking-wide text-on-surface-variant">
              {t("dashboardUi.home.moneySnapshotRetained")}
            </p>
            <p className="mt-1 font-headline text-xl font-bold text-secondary tabular-nums">
              {fmt(retainedMinor)}
            </p>
          </article>
          <article className="rounded-xl bg-surface-container-low p-4">
            <p className="text-label-sm uppercase tracking-wide text-on-surface-variant">
              {t("dashboardUi.home.moneySnapshotProjectedBalance")}
            </p>
            <p className="mt-1 font-headline text-xl font-bold text-tertiary tabular-nums">
              {fmt(projectedBankBalanceMinor)}
            </p>
          </article>
        </div>
      </section>

      {/* Rejilla tipo mockup */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-5">
        <motion.div
          className="min-w-0 overflow-hidden rounded-xl border border-primary/15 bg-primary/5 p-6 transition-colors hover:bg-primary/10"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 10 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? undefined : { duration: 0.24, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-4 flex min-w-0 items-start justify-between gap-2">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon name="Wallet" className="size-6" strokeWidth={1.5} aria-hidden />
            </div>
            <span className="min-w-0 shrink text-right font-headline text-xs font-bold leading-snug text-primary">
              {t("dashboardUi.home.gridSpentHint")}
            </span>
          </div>
          {/* Cifra principal: común + personal = total a aportar este mes (respuesta directa, sin desglose mental). */}
          <span className="block text-[10px] font-label uppercase tracking-widest text-primary/70">
            {t("dashboardUi.home.totalToContributeLabel")}
          </span>
          <p className="mt-1 min-w-0 break-words font-headline text-2xl font-extrabold tabular-nums text-primary">{fmt(s.spentMinor)}</p>
          <div className="mt-3 flex min-w-0 flex-col gap-2 border-t border-primary/10 pt-3">
            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-0">
              <span className="break-words text-[10px] font-label uppercase leading-snug tracking-widest text-slate-500">
                {t("dashboardUi.home.gridSpentSharedLine")}
              </span>
              <span className="whitespace-nowrap text-right font-headline text-sm font-bold tabular-nums text-secondary">
                {fmt(s.spentSharedMinor)}
              </span>
            </div>
            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-0">
              <span className="break-words text-[10px] font-label uppercase leading-snug tracking-widest text-slate-500">
                {t("dashboardUi.home.gridSpentPersonalLine")}
              </span>
              <span className="whitespace-nowrap text-right font-headline text-sm font-bold tabular-nums text-tertiary">
                {fmt(s.spentPersonalMinor)}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="rounded-xl bg-surface-container-low p-6 transition-colors hover:bg-surface-container-lowest"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 10 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? undefined : { duration: 0.24, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-4 flex items-start justify-between">
            <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon name="Receipt" className="size-6" strokeWidth={1.5} aria-hidden />
            </div>
            <span className="font-headline text-xs font-bold text-primary">{t("dashboardUi.home.gridForecastHint")}</span>
          </div>
          <span className="text-[10px] font-label uppercase tracking-widest text-slate-500">
            {t("dashboardUi.home.forecastExpensesMonth")}
          </span>
          <p className="font-headline text-xl font-bold text-primary">{fmt(s.forecastExpensesMinor)}</p>
        </motion.div>

        <motion.div
          className="rounded-xl bg-surface-container-low p-6 transition-colors hover:bg-surface-container-lowest"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 10 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? undefined : { duration: 0.24, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-4 flex items-start justify-between">
            <div className="flex size-12 items-center justify-center rounded-lg bg-secondary/5 text-secondary">
              <Icon name="ShoppingBasket" className="size-6" strokeWidth={1.5} aria-hidden />
            </div>
            <span className="font-headline text-xs font-bold text-secondary">{t("dashboardUi.home.gridBudgetHint")}</span>
          </div>
          <span className="text-[10px] font-label uppercase tracking-widest text-slate-500">
            {t("dashboardUi.home.plannedBudget")}
          </span>
          <p className="font-headline text-xl font-bold text-primary">{fmt(s.plannedBudgetMinor)}</p>
        </motion.div>

        <motion.div
          className="rounded-xl bg-surface-container-low p-6 transition-colors hover:bg-surface-container-lowest"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 10 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? undefined : { duration: 0.24, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-4 flex items-start justify-between">
            <div className="flex size-12 items-center justify-center rounded-lg bg-tertiary-container/10 text-tertiary">
              <Icon name="PieChart" className="size-6" strokeWidth={1.5} aria-hidden />
            </div>
            <span className="font-headline text-xs font-bold text-tertiary">{t("dashboardUi.home.gridUsageHint")}</span>
          </div>
          <span className="text-[10px] font-label uppercase tracking-widest text-slate-500">
            {t("dashboardUi.home.budgetUsage")}
          </span>
          <p className="font-headline text-xl font-bold text-primary">
            {s.budgetProgressPercent != null ? `${Math.round(s.budgetProgressPercent)}%` : "—"}
          </p>
        </motion.div>

        <motion.div
          className="rounded-xl border border-outline-variant/10 bg-surface-container-lowest p-6 shadow-sm"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 10 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? undefined : { duration: 0.24, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-4 flex items-center justify-between">
            <span className="text-[10px] font-label uppercase tracking-widest text-slate-400">
              {t("dashboardUi.home.gridBudgetMonthlyLabel")}
            </span>
            <span className="font-headline text-xs font-bold text-primary">{barPct}%</span>
          </div>
          <div className="perspective-progress mb-4 h-3 w-full overflow-hidden rounded-full bg-surface-container-highest">
            <div
              className="relative h-full bg-gradient-to-r from-primary to-primary-container"
              style={{ width: `${barPct}%` }}
            >
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    "linear-gradient(45deg,transparent 25%,rgba(255,255,255,0.2) 50%,transparent 75%)",
                  backgroundSize: "20px 20px",
                }}
              />
            </div>
          </div>
          <p className="text-[10px] text-slate-400">
            {t("dashboardUi.home.budgetProgressSubline", {
              spent: fmt(s.spentSharedMinor),
              cap: fmt(monthNeedMinor),
            })}
          </p>
        </motion.div>
      </div>

      <div className="mt-8 flex justify-center">
        <Link
          to={dashboardPaths.houseAnalytics(activeHouseId)}
          className="inline-flex items-center gap-2 rounded-xl bg-surface-container-low px-6 py-3 font-label text-sm font-bold text-primary shadow-sm outline outline-1 outline-outline-variant/10 transition-colors hover:bg-surface-container-lowest"
        >
          <Icon name="LineChart" className="size-5" strokeWidth={1.5} aria-hidden />
          {t("dashboardUi.analytics.openFromHome")}
        </Link>
      </div>

      {/* Ledger / próximos */}
      <section className="mt-14 lg:mt-16">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-label uppercase tracking-[0.2em] text-slate-400">
              {t("dashboardUi.home.ledgerEyebrow")}
            </span>
            <h3 className="font-headline text-2xl font-bold text-primary">{t("dashboardUi.home.ledgerTitle")}</h3>
          </div>
          <Link
            to={dashboardPaths.houseExpenses(activeHouseId)}
            className="border-b-2 border-primary pb-1 font-label text-xs font-bold uppercase tracking-widest text-primary"
          >
            {t("dashboardUi.home.ledgerViewAll")}
          </Link>
        </div>

        {(s.recentExpenses ?? []).length === 0 ? (
          <p className="rounded-xl bg-surface-container-low px-6 py-10 text-center font-body text-body-md text-on-surface-variant">
            {t("dashboardUi.home.ledgerRecentEmpty")}
          </p>
        ) : (
          <ul className="space-y-4">
            {(s.recentExpenses ?? []).map((e, i) => {
              const icon = journalIcons[i % journalIcons.length]!;
              const wrap = journalIconWrap[i % journalIconWrap.length]!;
              const lineMinor = expenseEffectiveMinor(e);
              return (
                <motion.li
                  key={e.id}
                  initial={prefersReducedMotion ? undefined : { opacity: 0, y: 8 }}
                  animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                  transition={
                    prefersReducedMotion
                      ? undefined
                      : { duration: 0.22, delay: Math.min(i, 6) * 0.05, ease: [0.22, 1, 0.36, 1] }
                  }
                >
                  <Link
                    to={dashboardPaths.houseExpenseDetail(activeHouseId, e.id)}
                    className="group flex items-center rounded-xl bg-surface-container-low p-6 transition-colors hover:bg-surface-container-lowest"
                  >
                    <div
                      className={cn(
                        "mr-6 flex size-12 shrink-0 items-center justify-center rounded-xl",
                        wrap,
                      )}
                    >
                      <Icon name={icon} className="size-6" strokeWidth={1.5} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-grow">
                      <h4 className="font-headline text-sm font-bold text-primary">{e.title}</h4>
                      <p className="text-xs text-slate-400">{formatShort(e.occurredOn)}</p>
                    </div>
                    <div className="mr-6 text-right">
                      <p className="font-headline text-sm font-bold text-primary">
                        {formatMinorAsCurrency(lineMinor, e.currency, locale)}
                      </p>
                      <span className="text-[10px] font-label uppercase text-slate-400">
                        {e.paidAt ? t("dashboardUi.expenses.paid") : t("dashboardUi.expenses.unpaid")}
                      </span>
                    </div>
                  </Link>
                </motion.li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
